import { createContext, useContext } from 'react';

/**
 * Element highlight API (ADR-0066). Provided once at the app root by `HelpHighlightProvider`;
 * the help panel, its field list and its tours drive it by `data-help-id`. Kept in its own file
 * (not the provider's .tsx) so react-refresh's only-export-components rule stays clean, the same
 * split as `ScreenHelpContext.tsx` / `useScreenHelp.ts`.
 */
export interface HelpHighlightApi {
  /** Scroll the first `[data-help-id="<id>"]` into view and draw the ring over it. False when not mounted. */
  highlight(id: string): boolean;
  /** Two 300 ms rings, then clear. False when not mounted. */
  pulse(id: string): boolean;
  clear(): void;
  isMounted(id: string): boolean;
}

/** Attribute-selector-safe quoting (jsdom has no `CSS.escape`). */
function quoteAttr(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}

export function findHelpTarget(id: string): HTMLElement | null {
  if (!id || typeof document === 'undefined') return null;
  return document.querySelector<HTMLElement>(`[data-help-id="${quoteAttr(id)}"]`);
}

/** Outside a provider (isolated unit tests): only `isMounted` does real work. */
const NOOP_API: HelpHighlightApi = {
  highlight: () => false,
  pulse: () => false,
  clear: () => {},
  isMounted: (id) => findHelpTarget(id) != null,
};

export const HelpHighlightContext = createContext<HelpHighlightApi>(NOOP_API);

export function useHelpHighlight(): HelpHighlightApi {
  return useContext(HelpHighlightContext);
}
